import type { RootState } from '@/app/store'
import type { AGGridFilterModel } from '@/types/agGridFilters'
import type { PageConfigRequest } from '@/types/api/page-config'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import { isEqual } from 'lodash'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { Page } from '@/types/schema/page-configs'
import {
  type AllocationListConfig,
  defaultAllocationListConfig,
} from '@/types/schema/page-configs/allocation-list'
import { decodeFilter } from '@/utils/decode-filters'

const ALLOCATION_LIST_SCHEMA_VERSION = '1.0'

interface AllocationListState {
  config: AllocationListConfig
  configVersion: string
  defaultConfig: AllocationListConfig
  searchTerm: string | null
  filters: AGGridFilterModel | null
}

const initialState: AllocationListState = {
  config: defaultAllocationListConfig,
  configVersion: ALLOCATION_LIST_SCHEMA_VERSION,
  defaultConfig: defaultAllocationListConfig,
  searchTerm: null,
  filters: null,
}

const allocationListSlice = createSlice({
  name: 'allocationList',
  reducerPath: 'allocationList',
  initialState,
  reducers: {
    initializeAllocationListConfig: handleInitializeAllocationListConfig,
    resetAllocationListConfig: handleResetAllocationListConfig,
    setAllocationListConfig: handleSetAllocationListConfig,
    reset: handleReset,
    resetSearchTerm: handleResetSearchTerm,
    setSearchTerm: handleSetSearchTerm,
    setFilters: handleSetFilters,
  },
})

function handleInitializeAllocationListConfig(
  state: AllocationListState,
  action: PayloadAction<{ config: AllocationListConfig; version: string }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  state.defaultConfig = action.payload.config
}

function handleResetAllocationListConfig(state: AllocationListState) {
  state.config = state.defaultConfig
}

function handleSetAllocationListConfig(
  state: AllocationListState,
  action: PayloadAction<Partial<AllocationListConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleReset(state: AllocationListState) {
  state.searchTerm = null
  state.filters = null
}

function handleSetSearchTerm(
  state: AllocationListState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleResetSearchTerm(state: AllocationListState) {
  state.searchTerm = null
}

function handleSetFilters(
  state: AllocationListState,
  action: PayloadAction<string>,
) {
  // If the filter is the same, don't update the state
  const payloadJson = action.payload ? decodeFilter(action.payload) : null
  if (isEqual(state.filters, payloadJson)) {
    return
  }
  if (!action.payload || action.payload === '') {
    state.filters = null
    return
  }
  state.filters = JSON.parse(decodeURIComponent(action.payload))
}

export const persistAllocationListConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  allocationListSlice.reducerPath + '/persistAllocationListConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).allocationList
    try {
      const request: PageConfigRequest = {
        id: Page.AllocationList,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeAllocationListConfig,
  resetAllocationListConfig,
  setAllocationListConfig,

  setSearchTerm,
  setFilters,
} = allocationListSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default allocationListSlice
